import { Component, Input, OnChanges, Output, EventEmitter, OnDestroy, OnInit } from '@angular/core';
import { EnglishProblemOutlineData } from '../english-problem-outline/english-problem-outline.component';
import { RoutingPathResolver } from 'src/app/app-routing-path-resolver';
import { ReadableEnglishProblem, ReadableEnglishPlaylistProblem } from 'src/app/models/problem';
import { Log } from 'src/app/utils/log';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { BookmarkProblem } from 'src/app/models/bookmark';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { openWindow, redirectPage } from '../../../actions/core.actions';
import * as BookmarkProblemListActions from 'src/app/actions/bookmark-problem-list.actions';
import * as BookmarkProblemsActions from 'src/app/actions/bookmark-problems.actions';
import { AppEvent } from '../../../models/event-log';
import { reportAppEvent } from '../../../actions/event-log.actions';
import { Store } from '@ngrx/store';
import { RootState } from '../../../reducers';
import { WordFileUtil } from '../../../utils/word-file-util';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatTableDataSource } from '@angular/material/table';
import * as BookmarkProblemsSelectors from 'src/app/selectors/bookmark-problems.selectors';
import * as BookmarkProblemListSelectors from 'src/app/selectors/bookmark-problem-list.selectors';
import { BookmarkProblemListWithCheck } from '../../widgets/button-toggle-bookmark-with-popover/button-toggle-bookmark-with-popover.component';
import {
  DIALOG_ZERO_PADDING_PANEL_CLASS,
  UNCATEGORIZED_BOOKMARK_PROBLEM_LISTS_DEFAULT_MAX_COUNT,
  WORD_DOWNLOAD_ALERT_DIALOG_WIDTH
} from 'src/app/resources/config';
import { BookmarkProblemListsUtil } from 'src/app/utils/bookmark-problem-lists-util';
import {
  BookmarkProblemListTableData,
  FindMyBookmarkProblemListParams,
  FindSharedBookmarkProblemListParams
} from 'src/app/models/bookmark-problem-list';
import { BookmarkProblemsCollection, FindBookmarkProblemsParams } from 'src/app/models/bookmark-problem';
import { User } from 'src/app/models/user';
import { getSignedInUser } from 'src/app/selectors/auth.selectors';
import * as BookmarkSelectors from 'src/app/selectors/bookmark.selectors';
import * as BookmarkActions from 'src/app/actions/bookmark.actions';
import { MatLegacyDialog as MatDialog, MatLegacyDialogConfig as MatDialogConfig } from '@angular/material/legacy-dialog';
import { WordDownloadAlertDialogComponent } from '../word-download-alert-dialog/word-download-alert-dialog.component';
import { TabVisibilityService } from 'src/app/services/tab-visibility.service';

interface ProblemData {
  sequentialId: string;
  id: string;
  university: string;
  departments: string;
  year: number;
  level: string;
  book: string;
  page: number;
  problemNumber: string;
  outlineData: EnglishProblemOutlineData;

  comment?: string;
  pdfPath?: string;
  pdfDownloading?: boolean;

  hasExternalData: boolean;
  hasWordData: boolean;
  isBookmarked?: boolean;

  bookmarkProblemLists?: BookmarkProblemListWithCheck[];
}

type ProblemId = string;

@Component({
  selector: 'app-english-problems',
  templateUrl: './english-problems.component.html',
  styleUrls: ['./english-problems.component.scss']
})
export class EnglishProblemsComponent implements OnInit, OnChanges, OnDestroy {
  private LOG_SOURCE = this.constructor.name;

  @Input() readableEnglishProblems: ReadableEnglishProblem[];
  @Input() longSentenceKeywords: string[];
  @Input() readableEnglishPlaylistProblems: ReadableEnglishPlaylistProblem[];
  @Input() problemButtonDisabled = false;
  @Input() wordButtonDisabled = false;
  @Input() hideLastBottomBorder = false;
  @Input() bookmarkedAllFlag: boolean;
  @Input() isTrial: boolean;
  @Input() playListId: string;
  @Input() themeId: string;
  @Input() isDraggable: boolean;
  @Input() isForPrint = false;
  @Input() isShowBookmark = true;
  @Input() disabled: boolean;
  @Input() bookmarkProblemListId: string;
  @Input() subjectId: string;

  @Output() problemClick = new EventEmitter<{ problemId: ProblemId; year: number }>();
  @Output() dropped = new EventEmitter<{ bookmarkProblemListId: string; problemIds: string[] }>();
  @Output() initialized = new EventEmitter<void>();
  @Output() totalProblemCount = new EventEmitter<{ problemCount: number }>();

  columns: string[];
  dataSource = new MatTableDataSource<ProblemData>([]);
  isDragging = false;
  bookmarkProblemLists: BookmarkProblemListWithCheck[] = [];
  signedInUser$: Observable<User>;
  englishBookmarkProblemLists$: Observable<BookmarkProblemListTableData[]>;
  englishBookmarkProblems$: Observable<BookmarkProblemsCollection[]>;
  englishUncategorizedBookmarkProblems$: Observable<BookmarkProblem[]>;
  uncategorizedBookmarkProblemCount$: Observable<number>;
  problemMaxCount: number;

  private objectUrls: string[] = [];
  private visibilitySub?: Subscription;

  constructor(private store: Store<RootState>, private dialog: MatDialog, private tabVisibility: TabVisibilityService) {}

  ngOnInit() {
    // 検索結果一覧印刷画面では何もしない
    if (this.isForPrint) return;

    // 検索結果や問題詳細のタブが再度開かれた場合は、データを取得し直してテーブルにデータを設定(別タブで更新があった場合に反映するため)
    this.visibilitySub = this.tabVisibility.onVisibilityChange().subscribe(state => {
      if (state === 'visible') {
        Log.debug(this.LOG_SOURCE, '英語の問題コンポーネントのあるタブが再度開かれました ', state);
        this.setUpBookmarkProblemLists();
        this.setUpBookmarkProblems();
        this.ngOnChanges();
      }
      return;
    });
  }

  ngOnChanges() {
    if (!this.readableEnglishProblems && !this.readableEnglishPlaylistProblems) return;
    const dataCount = this.readableEnglishProblems
      ? this.readableEnglishProblems.length
      : this.readableEnglishPlaylistProblems
      ? this.readableEnglishPlaylistProblems.length
      : 'none';
    Log.debug(this.LOG_SOURCE, `input data length: ${dataCount}`);
    if (dataCount !== undefined) this.totalProblemCount.emit({ problemCount: dataCount === 'none' ? 0 : dataCount });

    this.columns = this.readableEnglishProblems
      ? ['university', 'departments', 'year', 'outline', 'level', 'book', 'page', 'problemNumber', 'wordDownload']
      : ['sequentialId', 'university', 'departments', 'year', 'outline', 'level', 'book', 'page', 'problemNumber', 'wordDownload'];

    if (this.isShowBookmark) {
      this.columns.splice(0, 0, 'bookmark');
    }

    if (this.isForPrint) {
      this.dataSource.data = this.readableEnglishProblems
        ? this.readableEnglishProblems.map(readableProblem => this.mapDataForPrint(readableProblem))
        : [];
      return;
    }

    // お気に入り登録モダルで必要なデータ - お気に入りリスト一覧のデータ
    this.signedInUser$ = this.store.select(getSignedInUser).pipe(filter<User>(it => it != null));
    this.englishBookmarkProblemLists$ = combineLatest([
      this.store.select(BookmarkProblemListSelectors.getBookmarkProblemLists).pipe(filter(it => it != null)),
      this.signedInUser$
    ]).pipe(
      map(([bookmarkLists, user]) => {
        return bookmarkLists.filter(bookmarkList => bookmarkList.subjectId === this.subjectId && bookmarkList.userId === user.id);
      })
    );

    // お気に入り登録モダルで必要なデータ - 各お気に入りリストに紐づく問題データ
    this.englishBookmarkProblems$ = combineLatest([
      this.signedInUser$,
      this.store.select(BookmarkProblemsSelectors.getBookmarkProblems).pipe(filter(it => it != null))
    ]).pipe(
      map(([user, bookmarkProblems]) => {
        return bookmarkProblems.filter(
          bookmarkProblem => bookmarkProblem.subjectId === this.subjectId && bookmarkProblem.userId === user.id
        );
      })
    );

    // お気に入り登録モダルで必要なデータ - 未分類のお気に入りリストに紐づく問題データ
    this.store.dispatch(BookmarkActions.initializeFindBookmarkState());
    this.englishUncategorizedBookmarkProblems$ = combineLatest([this.signedInUser$]).pipe(
      switchMap(([user]) => {
        this.store.dispatch(BookmarkActions.findBookmark({ userId: user.id }));
        return this.store.select(BookmarkSelectors.getBookmark).pipe(
          filter(bookmark => !!bookmark),
          map(bookmark => (bookmark.problems ?? []).filter(problem => problem.subjectId === this.subjectId))
        );
      })
    );
    this.uncategorizedBookmarkProblemCount$ = combineLatest([this.signedInUser$]).pipe(
      switchMap(([user]) => {
        this.store.dispatch(BookmarkActions.findBookmark({ userId: user.id }));
        return this.store.select(BookmarkSelectors.getBookmark).pipe(
          filter(bookmark => !!bookmark),
          map(bookmark => (bookmark.problems ?? []).filter(problem => problem.subjectId === this.subjectId).length)
        );
      })
    );

    combineLatest([
      this.englishBookmarkProblemLists$,
      this.englishBookmarkProblems$,
      this.englishUncategorizedBookmarkProblems$,
      this.signedInUser$,
      this.uncategorizedBookmarkProblemCount$
    ])
      .pipe(take(1))
      .subscribe(([bookmarkProblemLists, bookmarkProblems, uncategorizedBookmarkProblems, user, uncategorizedProblemCount]) => {
        // 未分類のお気に入りリストに登録できる上限の数を取得
        const isUncategorizedProblemCountMaxExceeded = uncategorizedProblemCount >= UNCATEGORIZED_BOOKMARK_PROBLEM_LISTS_DEFAULT_MAX_COUNT;
        // お気に入りリストに登録できる上限の数を取得
        this.problemMaxCount = BookmarkProblemListsUtil.getProblemsMaxCount(user.schoolId);

        this.dataSource.data = this.readableEnglishProblems
          ? this.readableEnglishProblems.map(readableProblem =>
              this.mapData(
                readableProblem,
                bookmarkProblemLists,
                bookmarkProblems,
                uncategorizedBookmarkProblems,
                this.problemMaxCount,
                isUncategorizedProblemCountMaxExceeded
              )
            )
          : this.readableEnglishPlaylistProblems
          ? this.readableEnglishPlaylistProblems.map(readablePlaylistProblem => {
              const data = this.mapData(
                readablePlaylistProblem,
                bookmarkProblemLists,
                bookmarkProblems,
                uncategorizedBookmarkProblems,
                this.problemMaxCount,
                isUncategorizedProblemCountMaxExceeded
              );
              data.sequentialId = readablePlaylistProblem.sequentialId;
              if (readablePlaylistProblem.comment) {
                data.outlineData.comment = readablePlaylistProblem.comment;
              }
              if (readablePlaylistProblem.pdfPath) {
                data.pdfPath = readablePlaylistProblem.pdfPath;
                data.pdfDownloading = false;
              }
              return data;
            })
          : [];

        setTimeout(() => {
          this.initialized.emit();
        }, 100);
      });
  }

  ngOnDestroy() {
    this.objectUrls.forEach(url => URL.revokeObjectURL(url));
    this.visibilitySub?.unsubscribe();
  }

  showPlayListProblemDetail(problem: ProblemData) {
    const url = RoutingPathResolver.resolvePlaylistProblemDetail(this.playListId, this.themeId, problem.id);
    this.store.dispatch(openWindow({ url }));
  }

  openProblemWord(problem: ProblemData) {
    const wordUrl = WordFileUtil.getPath(problem.id, this.isTrial);
    if (wordUrl === '') {
      Log.warn(this.LOG_SOURCE, `wordURL が設定されていません. problem id: `, problem.id);
      return;
    }
    Log.debug(this.LOG_SOURCE, `word をダウンロードして開きます. wordURL: `, wordUrl);

    const isEdge = /Edg/.test(navigator.userAgent);
    const doNotWordDownloadAlertShowAgain = localStorage.getItem('doNotWordDownloadAlertShowAgain');
    if (isEdge && doNotWordDownloadAlertShowAgain !== 'true') {
      this.wordAlertDialog(problem.id, wordUrl);
      return;
    }

    this.store.dispatch(redirectPage({ url: wordUrl }));

    const subjectId = problem.id.slice(6, 8);
    const selectedProblemWord = `${problem.id}.docx`;
    const appEvent: AppEvent = {
      type: 'select-problem-word',
      value: JSON.stringify({
        subjectId,
        selectedProblemWord
      })
    };
    setTimeout(() => this.store.dispatch(reportAppEvent({ event: appEvent })));
  }

  wordAlertDialog(problemId: string, wordUrl: string) {
    const config: MatDialogConfig = {
      width: WORD_DOWNLOAD_ALERT_DIALOG_WIDTH,
      panelClass: DIALOG_ZERO_PADDING_PANEL_CLASS,
      autoFocus: false,
      data: { problemId, wordUrl },
      disableClose: true
    };
    this.dialog.open(WordDownloadAlertDialogComponent, config);
  }

  onDrop(event: CdkDragDrop<ProblemData[]>) {
    this.isDragging = false;
    if (event.previousIndex === event.currentIndex) return;

    const data = this.dataSource.data.slice(); // コピーを取る
    moveItemInArray(data, event.previousIndex, event.currentIndex);
    this.dataSource.data = data;
    const problemIds = data.map(problem => problem.id);

    // 問題の並び替えAPIを呼び出す
    Log.debug(this.LOG_SOURCE, 'お気に入りリストの問題の並び替えを行います。', problemIds);

    this.dropped.emit({ bookmarkProblemListId: this.bookmarkProblemListId, problemIds });
  }

  onDragStarted(event: any): void {
    this.isDragging = true;
  }

  onDragEnded(event: any): void {
    this.isDragging = false;
  }

  updateBookmarkProblemListPerProblem(event: {
    problemId: string;
    newBookmarkLists: BookmarkProblemListWithCheck[];
    newIsBookmarked: boolean;
  }) {
    // 各問題のお気に入り登録状態を更新
    const problem = this.dataSource.data.find(p => p.id === event.problemId);
    if (problem) {
      problem.bookmarkProblemLists = event.newBookmarkLists;
      problem.isBookmarked = event.newIsBookmarked;
    }
    this.dataSource.data = [...this.dataSource.data];
  }

  updateMaxCountFlag(event: { newBookmarkLists: BookmarkProblemListWithCheck[]; newIsBookmarked: boolean; problemId: string }) {
    // 問題数の上限フラグが更新された場合は、全ての問題のbookmarkProlemListsの該当リストIDのisProblemCountMaxExceededを更新する
    this.dataSource.data = this.dataSource.data.map(problem => {
      if (problem.id === event.problemId) {
        return {
          ...problem,
          bookmarkProblemLists: event.newBookmarkLists,
          isBookmarked: event.newIsBookmarked // お気に入り登録フラグ(isBookmarked)は問題IDに対して更新
        };
      } else {
        return {
          ...problem,
          bookmarkProblemLists: problem.bookmarkProblemLists.map(list => {
            const updated = event.newBookmarkLists.find(updatedList => updatedList.listId === list.listId);
            return updated ? { ...list, isProblemCountMaxExceeded: updated.isProblemCountMaxExceeded } : list;
          })
        };
      }
    });
  }

  updateNewBookmarkProblemLists(event: {
    newBookmarkLists: BookmarkProblemListWithCheck[];
    isBookmarked: boolean;
    newListIds: string[];
    problemId: string;
  }) {
    const newLists = event.newBookmarkLists.filter(list => event.newListIds.includes(list.listId));
    this.dataSource.data = this.dataSource.data.map(problem => {
      const updatedLists =
        problem.id === event.problemId
          ? event.newBookmarkLists
          : [...(problem.bookmarkProblemLists || []), ...newLists.map(list => ({ ...list, checked: false }))];

      const collator = new Intl.Collator('ja', { sensitivity: 'base' });
      const sortedLists = updatedLists.sort((a, b) => {
        // 未分類のリストのを先頭に、その他のリストは名前順
        if (a.listId === `uncategorized_${this.subjectId}`) return -1;
        if (b.listId === `uncategorized_${this.subjectId}`) return 1;
        return collator.compare(a.name, b.name);
      });

      return {
        ...problem,
        isBookmarked: problem.id === event.problemId ? event.isBookmarked : problem.isBookmarked,
        bookmarkProblemLists: sortedLists
      };
    });
  }

  private mapData(
    readableProblem: ReadableEnglishProblem,
    englishBookmarkProblemLists: BookmarkProblemListTableData[],
    englishBookmarkProblems: BookmarkProblemsCollection[],
    englishUncategorizedProblems: BookmarkProblem[],
    problemMaxCount: number,
    isUncategorizedProblemCountMaxExceeded: boolean
  ): ProblemData {
    const outlineData: EnglishProblemOutlineData = this.getOutlineData(readableProblem);

    const collator = new Intl.Collator('ja', { sensitivity: 'base' });

    const bookmarkProblemLists = englishBookmarkProblemLists
      .map(list => {
        const isProblemCountMaxExceeded =
          list.id === `uncategorized_${this.subjectId}`
            ? isUncategorizedProblemCountMaxExceeded
            : list.problemCount >= problemMaxCount
            ? true
            : false;
        const isListBookmarked =
          englishBookmarkProblems &&
          englishBookmarkProblems.some(problem => list.id === problem.bookmarkProblemListId && problem.problemId === readableProblem.id);
        const isUncategorizedChecked =
          englishUncategorizedProblems && englishUncategorizedProblems.some(problem => problem.problemId === readableProblem.id);

        return {
          listId: list.id,
          name: list.name,
          createdAt: list.createdAt,
          isProblemCountMaxExceeded,
          checked: list.id === `uncategorized_${this.subjectId}` ? isUncategorizedChecked : isListBookmarked
        };
      })
      .sort((a, b) => {
        // 未分類のリストのを先頭に、その他のリストは名前順
        if (a.listId === `uncategorized_${this.subjectId}`) return -1;
        if (b.listId === `uncategorized_${this.subjectId}`) return 1;
        return collator.compare(a.name, b.name);
      });

    const isBookmarked =
      this.bookmarkedAllFlag ||
      englishUncategorizedProblems?.some(problem => problem.problemId === readableProblem.id) ||
      englishBookmarkProblems?.some(englishBookmarkProblem => englishBookmarkProblem.problemId === readableProblem.id);

    const data = this.getProblemData(readableProblem, isBookmarked, bookmarkProblemLists);

    return data;
  }

  private mapDataForPrint(readableProblem: ReadableEnglishProblem): ProblemData {
    return this.getProblemData(readableProblem);
  }

  private getOutlineData(readableProblem: ReadableEnglishProblem): EnglishProblemOutlineData {
    const isThinking = readableProblem.thinking !== '';
    const outlineData: EnglishProblemOutlineData = {
      isThinking,
      fields: readableProblem.fields,
      subTypes: readableProblem.subTypes
    };
    if (readableProblem.longSentences && readableProblem.longSentences.length !== 0) {
      outlineData.longSentences = [...readableProblem.longSentences];
    }
    if (this.longSentenceKeywords && this.longSentenceKeywords.length !== 0) {
      outlineData.keywords = [...this.longSentenceKeywords];
    }
    if (readableProblem.highlight && readableProblem.highlight !== '') {
      outlineData.highlight = readableProblem.highlight;
    }
    return outlineData;
  }

  private getProblemData(
    readableProblem: ReadableEnglishProblem,
    isBookmarked: boolean = undefined,
    bookmarkProblemLists: any[] = undefined
  ): ProblemData {
    const outlineData: EnglishProblemOutlineData = this.getOutlineData(readableProblem);
    const data: ProblemData = {
      /** PlaylistProblem の場合は map 後に適切な値を設定 */
      sequentialId: '',

      id: readableProblem.id,
      university: readableProblem.university,
      departments: readableProblem.departments,
      year: readableProblem.year,
      level: readableProblem.level,
      book: readableProblem.book,
      page: readableProblem.page,
      problemNumber: readableProblem.problemNumber,
      hasExternalData: readableProblem.hasExternalData,
      hasWordData: readableProblem.hasWordData,
      outlineData,
      isBookmarked,
      bookmarkProblemLists
    };
    if (isBookmarked !== undefined) data.isBookmarked = isBookmarked;
    if (bookmarkProblemLists !== undefined) data.bookmarkProblemLists = bookmarkProblemLists;

    return data;
  }

  private setUpBookmarkProblemLists() {
    this.store.dispatch(BookmarkProblemListActions.initializeFindMyBookmarkProblemListsState());
    this.store.dispatch(BookmarkProblemListActions.initializeFindSharedBookmarkProblemListsState());

    combineLatest([this.signedInUser$])
      .pipe(take(1))
      .subscribe(([signedInUser]) => {
        const findMyBookmarkProblemListParams: FindMyBookmarkProblemListParams = {
          userId: signedInUser.id,
          subjectIds: signedInUser.visibleSubjectIds
        };
        this.store.dispatch(BookmarkProblemListActions.findMyBookmarkProblemLists(findMyBookmarkProblemListParams));

        const findSharedBookmarkProblemListParams: FindSharedBookmarkProblemListParams = {
          schoolId: signedInUser.schoolId,
          userId: signedInUser.id
        };
        this.store.dispatch(BookmarkProblemListActions.findSharedBookmarkProblemLists(findSharedBookmarkProblemListParams));
      });
  }

  private setUpBookmarkProblems() {
    this.store.dispatch(BookmarkProblemsActions.initializeFindBookmarkProblemsState());

    combineLatest([this.signedInUser$])
      .pipe(take(1))
      .subscribe(([signedInUser]) => {
        const findBookmarkProblemsParams: FindBookmarkProblemsParams = {
          userId: signedInUser.id,
          subjectIds: signedInUser.visibleSubjectIds
        };
        this.store.dispatch(BookmarkProblemsActions.findBookmarkProblems(findBookmarkProblemsParams));
      });
  }
}
